import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useCanvasApi } from "../../../context/CanvasContext";

const BASE = "https://archive.org/download/goose2025-06-28/goose2025-06-28";
const GOOSE_TRACKS = [
  `${BASE}s1t01.mp3`, // Factory Fiction
  `${BASE}s1t02.mp3`, // Hungersite
  `${BASE}s1t03.mp3`, // My Mind Has Been Consumed By Media
  `${BASE}s1t04.mp3`, // A Western Sun ->
  `${BASE}s1t05.mp3`, // Running Up That Hill
  `${BASE}s1t06.mp3`, // Give It Time
  `${BASE}s1t07.mp3`, // Feel It Now
  `${BASE}s1t08.mp3`, // Dustin Hoffman ->
  `${BASE}s1t09.mp3`, // Animal
  `${BASE}s2t01.mp3`, // Thatch
  `${BASE}s2t02.mp3`, // Red Bird ->
  `${BASE}s2t03.mp3`, // Don't Leave Me This Way
  `${BASE}s2t04.mp3`, // Tumble ->
  `${BASE}s2t05.mp3`, // Creatures ->
  `${BASE}s2t06.mp3`, // Shama Lama Ding Dong ->
  `${BASE}s2t07.mp3`, // Tumble (reprise)
  `${BASE}s2t08.mp3`, // Jed Stone ->
  `${BASE}s2t09.mp3`, // Dripfield
  `${BASE}s2t10.mp3`, // E: Arcadia
];

interface Station {
  label: string;
  desc: string;
  emoji: string;
  color: string;
  stream: string;
  /** Full playlist — when present, onEnded auto-advances track-by-track. */
  tracks?: string[];
}

export const SOMA_STATIONS: Station[] = [
  {
    label: "Groove Salad",
    desc: "Ambient · Electronic",
    emoji: "🌿",
    color: "#22c55e",
    stream: "https://ice1.somafm.com/groovesalad-128-mp3",
  },
  {
    label: "Lush",
    desc: "Indie · Folk",
    emoji: "🎸",
    color: "#a78bfa",
    stream: "https://ice1.somafm.com/lush-128-mp3",
  },
  {
    label: "Secret Agent",
    desc: "Lounge · Jazz",
    emoji: "🕵️",
    color: "#f59e0b",
    stream: "https://ice1.somafm.com/secretagent-128-mp3",
  },
  {
    label: "Drone Zone",
    desc: "Atmospheric · Ambient",
    emoji: "🌌",
    color: "#06b6d4",
    stream: "https://ice1.somafm.com/dronezone-128-mp3",
  },
  {
    label: "DEF CON Radio",
    desc: "Electronic · Industrial",
    emoji: "⚡",
    color: "#ec4899",
    stream: "https://ice1.somafm.com/defcon-128-mp3",
  },
  {
    label: "Goose",
    desc: "Live @ MSG · Jun 28, 2025",
    emoji: "🪿",
    color: "#f97316",
    stream: GOOSE_TRACKS[0],
    tracks: GOOSE_TRACKS,
  },
];

interface MusicWidgetProps {
  id: string;
  data: {
    stationIndex?: number;
    [key: string]: unknown;
  };
  dragging?: boolean;
}

export const MusicWidget = memo(function MusicWidget({
  id,
  data,
  dragging,
}: MusicWidgetProps) {
  const canvas = useCanvasApi();

  const raw = parseInt(String(data.stationIndex ?? 0));
  const stationIndex = Math.max(
    0,
    Math.min(SOMA_STATIONS.length - 1, isNaN(raw) ? 0 : raw),
  );
  const station = SOMA_STATIONS[stationIndex];

  // ── Audio state (local — playback is per-user) ────────────────────────────
  const audioRef = useRef<HTMLAudioElement>(null);
  const trackIndexRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);

  // ── Web Audio API frequency analyser ─────────────────────────────────────
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const animFrameRef = useRef<number>(0);
  const [freqBands, setFreqBands] = useState<number[]>(new Array(9).fill(0));

  /** Connect the audio element to an AnalyserNode on first play. */
  const initAnalyser = useCallback(() => {
    if (!audioRef.current || sourceRef.current) return; // already initialised
    try {
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128; // 64 frequency bins — plenty for 9 columns
      analyser.smoothingTimeConstant = 0.75;
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
    } catch {
      // Web Audio not available — visualiser silently disabled
    }
  }, []);

  /** Drive the dot grid at ~30fps while playing. */
  useEffect(() => {
    const analyser = analyserRef.current;
    if (!isPlaying || !analyser) {
      setFreqBands(new Array(9).fill(0));
      return;
    }
    const buf = new Uint8Array(analyser.frequencyBinCount); // 64 bins
    const NUM_COLS = 9;
    const tick = () => {
      analyser.getByteFrequencyData(buf);
      // Map 64 bins → 9 bands (logarithmic-ish: weight lower frequencies more)
      const bands = Array.from({ length: NUM_COLS }, (_, col) => {
        const start = Math.floor((col / NUM_COLS) ** 1.4 * buf.length);
        const end = Math.floor(((col + 1) / NUM_COLS) ** 1.4 * buf.length);
        const slice = buf.slice(Math.max(start, 0), Math.max(end, start + 1));
        return slice.reduce((s, v) => s + v, 0) / slice.length;
      });
      setFreqBands(bands);
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying]);

  // Reset track position and sync audio src whenever station changes.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const wasPlaying = !audio.paused;
    trackIndexRef.current = 0;
    audio.src = station.stream;
    audio.volume = volume;
    setIsError(false);
    if (wasPlaying) {
      setIsLoading(true);
      audio.play().catch(() => {
        setIsPlaying(false);
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationIndex]);

  // Sync volume to audio element.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      setIsError(false);
      if (!audio.src) audio.src = station.stream;
      initAnalyser();
      // Resume suspended AudioContext (required by autoplay policy)
      if (audioCtxRef.current?.state === "suspended") {
        await audioCtxRef.current.resume();
      }
      setIsLoading(true);
      try {
        await audio.play();
      } catch {
        setIsPlaying(false);
        setIsLoading(false);
        setIsError(true);
      }
    }
  }, [isPlaying, station.stream, initAnalyser]);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = !isMuted;
    audio.muted = next;
    setIsMuted(next);
  }, [isMuted]);

  const goNext = useCallback(() => {
    canvas.updateNodeData(id, {
      stationIndex: (stationIndex + 1) % SOMA_STATIONS.length,
    });
  }, [stationIndex, id, canvas]);

  // Advance to the next track when a playlist-based station's track ends.
  const handleTrackEnded = useCallback(() => {
    const playlist = station.tracks;
    if (!playlist || playlist.length === 0) return;
    const nextIdx = (trackIndexRef.current + 1) % playlist.length;
    trackIndexRef.current = nextIdx;
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = playlist[nextIdx];
    setIsLoading(true);
    audio.play().catch(() => {
      setIsLoading(false);
    });
  }, [station.tracks]);

  // ── Speaker dot grille / frequency visualiser (6 rows × 9 cols) ────────────
  const NUM_ROWS = 6;
  const dots = Array.from({ length: 54 }, (_, i) => {
    const row = Math.floor(i / 9);
    const col = i % 9;

    if (isPlaying && freqBands[col] !== undefined) {
      // VU-meter: fill from bottom (row 5) upward based on band amplitude
      const amplitude = freqBands[col] / 255;
      const threshold = (NUM_ROWS - 1 - row) / NUM_ROWS; // row 5 → 0, row 0 → 5/6
      const isActive = amplitude >= threshold;
      const dotOpacity = isActive ? Math.max(0.35, amplitude) : 0.08;
      return (
        <div
          key={i}
          style={{
            width: 4,
            height: 4,
            borderRadius: "50%",
            background: isActive ? station.color : "rgba(255,255,255,.08)",
            opacity: dotOpacity,
            transition: "opacity .04s, background .04s",
          }}
        />
      );
    }

    // Static speaker grille when paused
    const edgeDist = Math.min(row, 5 - row, col, 8 - col);
    const baseOp = edgeDist === 0 ? 0.22 : edgeDist === 1 ? 0.38 : 0.55;
    return (
      <div
        key={i}
        style={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          background: `rgba(255,255,255,${baseOp})`,
        }}
      />
    );
  });

  return (
    <>
      <style>{`
        @keyframes _mus_spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        ._mus_vinyl { animation: _mus_spin 4s linear infinite; animation-play-state: ${isPlaying ? "running" : "paused"}; transition: box-shadow .2s, filter .2s; }
        ._mus_vinyl_wrap { transition: transform .2s; }
        ._mus_vinyl_wrap:hover { transform: scale(1.06); }
        ._mus_vinyl_wrap:hover ._mus_vinyl { filter: brightness(1.18); }
        ._mus_vol::-webkit-slider-thumb { appearance: none; width: 12px; height: 12px; border-radius: 50%; background: white; cursor: pointer; }
        ._mus_vol::-moz-range-thumb { width: 12px; height: 12px; border-radius: 50%; background: white; cursor: pointer; border: none; }
      `}</style>

      {/* Hidden audio element — crossOrigin required for Web Audio API analyser */}
      <audio
        ref={audioRef}
        crossOrigin="anonymous"
        onPlay={() => {
          setIsPlaying(true);
          setIsLoading(false);
        }}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
        onEnded={handleTrackEnded}
        onError={() => {
          setIsError(true);
          setIsPlaying(false);
          setIsLoading(false);
        }}
      />

      {/* Root card — no nodrag here so React Flow can drag the node from any non-interactive area */}
      <div
        style={{
          width: 296,
          background: "rgba(10,10,20,.8)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderRadius: 20,
          overflow: "hidden",
          border: "1.5px solid rgba(255,255,255,.08)",
          fontFamily: "-apple-system,BlinkMacSystemFont,sans-serif",
          opacity: dragging ? 0.8 : 1,
        }}
      >
        {/* ── Vinyl + speaker grille ────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "18px 18px 14px",
          }}
        >
          {/* Vinyl — click to cycle to next station */}
          <div
            onClick={goNext}
            onPointerDown={(e) => e.stopPropagation()}
            className="_mus_vinyl_wrap"
            style={{
              position: "relative",
              width: 90,
              height: 90,
              flexShrink: 0,
              cursor: "pointer",
              pointerEvents: "all",
            }}
          >
            <div
              className="_mus_vinyl"
              style={{
                width: 90,
                height: 90,
                borderRadius: "50%",
                background: `radial-gradient(circle,
                  ${station.color}44 0%,
                  #1a1a1a 30%, #111 38%,
                  #1c1c1c 44%, #0e0e0e 50%,
                  #1c1c1c 56%, #0e0e0e 64%,
                  #1c1c1c 70%, #0e0e0e 78%,
                  #111 84%, #080808 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 0 2px rgba(255,255,255,.05), 0 6px 24px rgba(0,0,0,.70), 0 0 20px ${station.color}22`,
              }}
            >
              {/* Spindle */}
              <div
                style={{
                  position: "absolute",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#0a0a0a",
                  border: "1px solid rgba(255,255,255,.06)",
                }}
              />
              {/* Center label */}
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: station.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 17,
                  boxShadow: `0 0 0 2px rgba(0,0,0,.5), 0 0 14px ${station.color}66`,
                }}
              >
                {isLoading ? (
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      border: "2px solid rgba(255,255,255,.3)",
                      borderTopColor: "white",
                      borderRadius: "50%",
                      animation: "_mus_spin .7s linear infinite",
                      display: "inline-block",
                    }}
                  />
                ) : (
                  station.emoji
                )}
              </div>
            </div>
          </div>

          {/* Speaker grille */}
          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "repeat(9,1fr)",
              gap: 4,
              padding: "6px 0",
            }}
          >
            {dots}
          </div>
        </div>

        {/* ── Divider ─────────────────────────────────────────── */}
        <div
          style={{
            height: 1,
            background: "rgba(255,255,255,.15)",
            margin: "0 18px",
          }}
        />

        {/* ── Station info + controls ──────────────────────────── */}
        <div style={{ padding: "10px 14px 13px" }}>
          {/* Station name row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: station.color,
                  letterSpacing: ".01em",
                  marginBottom: 2,
                  filter: "brightness(1.3)",
                }}
              >
                {station.label}
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: "rgba(255,255,255,.55)",
                  letterSpacing: ".05em",
                }}
              >
                {isError
                  ? "⚠ stream unavailable"
                  : isLoading
                    ? "Connecting…"
                    : isPlaying
                      ? `● LIVE · ${station.desc}`
                      : station.desc}
              </div>
            </div>
          </div>

          {/* Controls row: play/pause + volume */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 10 }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* Play / Pause */}
            <button
              onClick={togglePlay}
              style={{
                cursor: "pointer",
                pointerEvents: "all",
                border: "none",
                width: 34,
                height: 34,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                background: isPlaying ? station.color : "rgba(255,255,255,.12)",
                boxShadow: isPlaying ? `0 2px 12px ${station.color}66` : "none",
                transition: "background .2s, box-shadow .2s",
              }}
            >
              {isPlaying ? (
                <span style={{ display: "inline-flex", gap: 3 }}>
                  <span
                    style={{
                      width: 3,
                      height: 12,
                      background: "currentColor",
                      borderRadius: 1.5,
                    }}
                  />
                  <span
                    style={{
                      width: 3,
                      height: 12,
                      background: "currentColor",
                      borderRadius: 1.5,
                    }}
                  />
                </span>
              ) : (
                <svg
                  width="11"
                  height="13"
                  viewBox="0 0 11 13"
                  fill="currentColor"
                  style={{ marginLeft: 2 }}
                >
                  <path d="M0 0L11 6.5 0 13z" />
                </svg>
              )}
            </button>

            {/* Volume slider with interactive mute button */}
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              {/* Mute toggle — larger, interactive */}
              <button
                onClick={toggleMute}
                onPointerDown={(e) => e.stopPropagation()}
                className="nodrag"
                title={isMuted ? "Unmute" : "Mute"}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isMuted
                    ? "rgba(255,255,255,.25)"
                    : "rgba(255,255,255,.65)",
                  flexShrink: 0,
                  transition: "color .15s",
                }}
              >
                {isMuted ? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="currentColor"
                  >
                    <path d="M2 6h3l4-4v14l-4-4H2z" />
                    <line
                      x1="11"
                      y1="6"
                      x2="17"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                    <line
                      x1="17"
                      y1="6"
                      x2="11"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="currentColor"
                  >
                    <path d="M2 6h3l4-4v14l-4-4H2z" />
                    {volume > 0.05 && (
                      <path
                        d="M12 5a5 5 0 010 8"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        fill="none"
                        strokeLinecap="round"
                      />
                    )}
                    {volume > 0.5 && (
                      <path
                        d="M14 2.5a8 8 0 010 13"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        fill="none"
                        strokeLinecap="round"
                      />
                    )}
                  </svg>
                )}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.02}
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="_mus_vol nodrag"
                onPointerDown={(e) => e.stopPropagation()}
                style={{
                  flex: 1,
                  height: 3,
                  borderRadius: 2,
                  appearance: "none",
                  WebkitAppearance: "none",
                  background: isMuted
                    ? "rgba(255,255,255,.15)"
                    : `linear-gradient(to right, ${station.color} ${volume * 100}%, rgba(255,255,255,.15) ${volume * 100}%)`,
                  cursor: "pointer",
                  outline: "none",
                  border: "none",
                  pointerEvents: "all",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
});
